import { Component } from 'react'
import PropTypes from 'prop-types'

import {
    Cell,
    ListWrapper,
    Row,
    DragIcon,
    RemoveIcon,
    DownloadIcon,
    SelectedIcon,
    DragWrapper,
} from './List.style'

class List extends Component {
    static propTypes = {
        className: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.shape({})),
        display: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.func,
        ]).isRequired,
        draggable: PropTypes.bool,
        onClickRow: PropTypes.func,
        onDrop: PropTypes.func,
        onRemove: PropTypes.func,
        onDownload: PropTypes.func,
        highLighted: PropTypes.number,
        onlyBorderBottom: PropTypes.bool,
        selectedIndex: PropTypes.number,
        noBorders: PropTypes.bool,
        noBackGround: PropTypes.bool,
        itemRenderer: PropTypes.func,
        marginBottom: PropTypes.string,
        buildKey: PropTypes.func,
        width: PropTypes.string,
    }

    static defaultProps = {
        className: '',
        data: [],
        draggable: false,
        onClickRow: null,
        onDrop: () => null,
        onRemove: null,
        onDownload: null,
        highLighted: -1,
        selectedIndex: -1,
        onlyBorderBottom: false,
        noBorders: false,
        noBackGround: false,
        itemRenderer: null,
        marginBottom: null,
        buildKey: null,
        width: null,
    }

    constructor(props) {
        super(props)

        this.state = {
            isDragging: false,
            indexDragging: null,
            newIndex: null,
        }

        this.onDragEnd = this.onDragEnd.bind(this)
        this.onDragOver = this.onDragOver.bind(this)
        this.onDragStart = this.onDragStart.bind(this)
        this.onDrop = this.onDrop.bind(this)
    }

    /**
     * Get props data, but sorted
     */
    get sorteredData() {
        const { data } = this.props
        const { indexDragging, isDragging, newIndex } = this.state

        if (!isDragging) {
            return data
        }

        // Get dragged item
        const itemDragging = data[indexDragging]

        // Remove dragged item from data
        const items = data.filter((item, index) => index !== indexDragging)

        // Add item in the new position
        items.splice(newIndex, 0, itemDragging)

        return items
    }

    /**
     * Get items as the way they are going to be displayed
     */
    get items() {
        const { display } = this.props

        // Map by displaying field
        const items = this.sorteredData.map((item) => (display instanceof Function ? display(item) : item[display]))

        return items
    }

    /**
     * On Drag ends
     */
    onDragEnd() {
        this.setState({ isDragging: false, indexDragging: null })
    }

    /**
     * On drag is over a row
     * @param {Number} index Index of the element where the drag is over
     */
    onDragOver(event, index) {
        const { draggable } = this.props

        if (!draggable) {
            return
        }

        event.preventDefault()

        const { newIndex } = this.state

        if (newIndex !== index) {
            this.setState({ newIndex: index })
        }
    }

    /**
     * Starts drag
     * @param {Object} event
     * @param {Number} index Index of the dragged item
     */
    onDragStart(event, index) {
        const { draggable } = this.props

        if (!draggable) {
            return
        }

        this.setState({
            isDragging: true,
            indexDragging: index,
            newIndex: index,
        })
    }

    /**
     * Drops the item
     */
    onDrop() {
        const { draggable } = this.props

        if (!draggable) {
            return
        }

        const { onDrop } = this.props

        onDrop(this.sorteredData)
    }

    /**
     * Renders one row of the list
     * @param {Object} value
     * @param {Number} index
     */
    renderRow(value, index) {
        const {
            draggable,
            onClickRow,
            onRemove,
            onDownload,
            highLighted,
            onlyBorderBottom,
            selectedIndex,
            noBorders,
            noBackGround,
            itemRenderer,
            marginBottom,
            buildKey,
            width,
        } = this.props
        const { newIndex, isDragging } = this.state
        const key = buildKey ? buildKey(index) : index

        return (
            <Row
                key={key}
                clickable={!!onClickRow}
                draggable={draggable}
                isDragging={isDragging}
                isBeingDragged={isDragging && newIndex === index}
                onClick={onClickRow ? (e) => onClickRow(index, e) : () => null}
                onDragStart={(e) => this.onDragStart(e, index)}
                onDragEnd={this.onDragEnd}
                onDragOver={(e) => this.onDragOver(e, index)}
                highLighted={highLighted === index}
                onlyBorderBottom={onlyBorderBottom}
                noBorders={noBorders}
                noBackGround={noBackGround}
                noStaticHeight={itemRenderer}
                marginBottom={marginBottom}
                width={width}
            >
                {itemRenderer ? (
                    <>
                        {itemRenderer(key)}
                        {draggable && (
                            <>
                                <DragWrapper />
                                <DragIcon opaque icon="grip-lines-vertical" />
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <Cell>
                            {value}
                            {selectedIndex === index && <SelectedIcon icon="check" />}
                        </Cell>
                        {draggable && <DragIcon icon="grip-lines-vertical" />}
                        {onRemove && (
                            <RemoveIcon
                                icon="trash-alt"
                                onClick={(e) => {
                                    if (typeof e.stopPropagation === 'function') {
                                        e.stopPropagation()
                                    }
                                    onRemove(index)
                                }}
                            />
                        )}
                        {onDownload && (
                            <DownloadIcon
                                icon="download"
                                onClick={(e) => {
                                    if (typeof e.stopPropagation === 'function') {
                                        e.stopPropagation()
                                    }
                                    onDownload(index)
                                }}
                            />
                        )}
                    </>
                )}
            </Row>
        )
    }

    render() {
        const { className } = this.props
        const { isDragging } = this.state
        const { items } = this

        return (
            <ListWrapper isDragging={isDragging} onDrop={this.onDrop} className={className}>
                {items.map((value, index) => this.renderRow(value, index))}
            </ListWrapper>
        )
    }
}

export default List
